export function calculateAge(dateOfBirth: string | Date | null): number | string {
  if (!dateOfBirth) return 'Not provided';

  // Parse YYYY-MM-DD safely to avoid UTC interpretation quirks
  const birthDate: Date = typeof dateOfBirth === 'string' && !dateOfBirth.includes('T')
    ? (() => {
        const [y, m, d] = dateOfBirth.split('-').map(Number);
        return new Date(y, m - 1, d);
      })()
    : new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
} 

// Business timezone for all admin displays
export const BUSINESS_TIMEZONE = 'America/Los_Angeles';

type TimeOfDayParts = { hour: number; minute: number };

function parseTimeOfDay(input: string): TimeOfDayParts | null {
  // Accepts "HH:MM" or "HH:MM:SS"
  const [hhStr, mmStr] = input.split(':');
  const hour = Number(hhStr);
  const minute = Number(mmStr);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (hour < 0 || hour > 23) return null;
  if (minute < 0 || minute > 59) return null;
  return { hour, minute };
}

/**
 * Formats a TIME column (HH:MM or HH:MM:SS) as a human-readable time-of-day.
 * IMPORTANT: This does NOT perform timezone conversion; it treats the input as a business-local clock time.
 */
export function formatTimeOfDay(
  timeString: string,
  options?: { hour12?: boolean }
): string {
  if (!timeString) return '';
  const parts = parseTimeOfDay(timeString);
  if (!parts) return '';

  const hour12 = options?.hour12 ?? false;
  if (!hour12) {
    return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
  }

  const suffix = parts.hour >= 12 ? 'PM' : 'AM';
  const hour12Val = parts.hour % 12 === 0 ? 12 : parts.hour % 12;
  return `${hour12Val}:${String(parts.minute).padStart(2, '0')} ${suffix}`;
}

/**
 * Formats a Date's local hour/minute as time-of-day.
 * Useful for UI Date objects that are created from business-local slot times (no timezone conversion).
 */
export function formatTimeOfDayFromDate(
  date: Date,
  options?: { hour12?: boolean }
): string {
  const hour = date.getHours();
  const minute = date.getMinutes();
  return formatTimeOfDay(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`, options);
}

/**
 * Formats a date-like value in the business timezone (America/Los_Angeles)
 * to MM/DD/YYYY. Accepts either a full timestamp or a YYYY-MM-DD string.
 */
export function formatDateInBusinessTZ(input: string | Date): string {
  if (!input) return '';

  let date: Date;
  if (typeof input === 'string') {
    if (input.includes('T')) {
      // Full timestamp; construct Date directly (interpreted as UTC if Z/offset present)
      date = new Date(input);
    } else {
      // Date-only string; construct as local date to avoid UTC shift
      const [yearStr, monthStr, dayStr] = input.split('-');
      const year = Number(yearStr);
      const month = Number(monthStr);
      const day = Number(dayStr);
      if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return '';
      date = new Date(year, month - 1, day);
    }
  } else {
    date = input;
  }

  return new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
}

/**
 * Formats a time-like value in the business timezone (America/Los_Angeles)
 * to HH:mm by default (24-hour). Accepts full timestamp or Date.
 *
 * The hour is padded only in 24-hour form. A 24-hour clock pads by convention — `09:04` keeps
 * a column of times aligned — but nobody writes half past four in the afternoon as
 * `04:30 PM`, and every 12-hour caller here is a sentence a parent reads ("Checked in
 * 09:04 AM"), not a column.
 */
export function formatTimeInBusinessTZ(
  input: string | Date,
  options?: { hour12?: boolean }
): string {
  if (!input) return '';
  const date = typeof input === 'string' ? new Date(input) : input;
  const hour12 = options?.hour12 ?? false;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    hour: hour12 ? 'numeric' : '2-digit',
    minute: '2-digit',
    hour12,
  }).format(date);
}

/**
 * Formats a date-time value in the business timezone (America/Los_Angeles)
 * to 'MM/DD/YYYY HH:mm' by default (24-hour), using existing helpers.
 */
export function formatDateTimeInBusinessTZ(
  input: string | Date,
  options?: { hour12?: boolean }
): string {
  if (!input) return '';
  const date = typeof input === 'string' ? new Date(input) : input;
  return `${formatDateInBusinessTZ(date)} ${formatTimeInBusinessTZ(date, { hour12: options?.hour12 })}`;
}

/**
 * Formats a Date as YYYY-MM-DD using LOCAL date components (no UTC normalization).
 * Useful when you've constructed a local Date from year/month/day and want a stable DATE string.
 */
export function toISODateLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Today's date as YYYY-MM-DD in the business timezone.
 * Avoids relying on device timezone for admin surfaces.
 */
export function getTodayDateStringInBusinessTZ(): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  // en-CA yields YYYY-MM-DD
  return formatter.format(new Date());
}

/** Minutes the business timezone is ahead of UTC at a given instant (-420 in PDT, -480 in PST). */
function businessOffsetMinutes(at: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(at));
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(p => p.type === type)?.value);
  const wallAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
  return Math.round((wallAsUtc - Math.floor(at / 60000) * 60000) / 60000);
}

/**
 * The instant a business-local wall clock time happens — a `DATE` plus a `TIME` column, read in
 * America/Los_Angeles whatever the device's own timezone is. The same reading as Postgres'
 * `(date + time) AT TIME ZONE 'America/Los_Angeles'`, so a page and a trigger agree on when a
 * slot starts. Returns null for unparseable input.
 */
export function businessLocalToInstant(dateString: string, timeString: string): Date | null {
  const [year, month, day] = (dateString ?? '').split('-').map(Number);
  const time = parseTimeOfDay(timeString ?? '');
  if (!time || ![year, month, day].every(Number.isFinite)) return null;

  const wallAsUtc = Date.UTC(year, month - 1, day, time.hour, time.minute);
  // Guess with the offset at the wall time read as UTC, then re-read the offset at the guess:
  // the two differ only within hours of a DST change, and the second is the right one.
  const first = wallAsUtc - businessOffsetMinutes(wallAsUtc) * 60000;
  const second = wallAsUtc - businessOffsetMinutes(first) * 60000;
  return new Date(second);
}

/**
 * Formats a date string (YYYY-MM-DD) to display in local format
 * without timezone conversion issues.
 * 
 * When a date string like "2019-11-22" is parsed with new Date(),
 * it assumes UTC time which can cause timezone shifts.
 * This function ensures the date is displayed correctly regardless of timezone.
 */
export function formatDate(dateString: string): string {
  if (!dateString) return '';
  
  // Split the date string to get year, month, day
  const [year, month, day] = dateString.split('-').map(Number);
  
  // Create a date object with the local timezone
  const date = new Date(year, month - 1, day);
  
  // Format date to local string
  return date.toLocaleDateString();
} 

/**
 * Formats a date string (YYYY-MM-DD) or timestamp to display in MM/DD/YYYY format
 * without timezone conversion issues.
 * 
 * This function handles both date-only strings and full timestamps,
 * ensuring consistent display in MM/DD/YYYY format.
 */
export function formatDateDisplay(dateString: string): string {
  if (!dateString) return '';
  
  // Handle both date-only strings and timestamps
  const [datePart] = dateString.split('T'); // Split at T if it's a timestamp
  const [year, month, day] = datePart.split('-').map(Number);
  
  // Return in MM/DD/YYYY format
  return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
} 

/**
 * A `DATE` column as a weekday and a day — "Tue, Aug 18", or "Tuesday, Aug 18".
 *
 * For DATE columns only, like `formatDateDisplay` above: the parts are read off the string
 * and handed to a local `Date`, so nothing is converted and a session date cannot slide to
 * the previous day on a device east of the business timezone.
 */
export function formatWeekdayDateDisplay(
  dateString: string,
  { weekday = 'short' }: { weekday?: 'short' | 'long' } = {},
): string {
  if (!dateString) return '';
  const [datePart] = dateString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday,
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Gets today's date in YYYY-MM-DD format without timezone issues.
 * This is useful for comparing dates or setting default values.
 */
export function getTodayDateString(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Gets the current timestamp in ISO format with the local timezone offset.
 * This is useful for edge functions that need to store timestamps.
 * 
 * @returns {string} ISO timestamp with local timezone offset (e.g., "2024-03-20T15:30:00-07:00")
 */
export function getLocalISOString(): string {
  const now = new Date();
  const offset = -now.getTimezoneOffset();
  const offsetHours = Math.abs(Math.floor(offset / 60)).toString().padStart(2, '0');
  const offsetMinutes = Math.abs(offset % 60).toString().padStart(2, '0');
  const offsetSign = offset >= 0 ? '+' : '-';
  
  return now.getFullYear() +
    '-' + (now.getMonth() + 1).toString().padStart(2, '0') +
    '-' + now.getDate().toString().padStart(2, '0') +
    'T' + now.getHours().toString().padStart(2, '0') +
    ':' + now.getMinutes().toString().padStart(2, '0') +
    ':' + now.getSeconds().toString().padStart(2, '0') +
    offsetSign + offsetHours + ':' + offsetMinutes;
}

/**
 * Converts a UTC timestamp to local time ISO string.
 * This is useful when receiving timestamps from external services.
 * 
 * @param {string} utcString - UTC timestamp string
 * @returns {string} ISO timestamp with local timezone offset
 */
export function utcToLocalISOString(utcString: string): string {
  const utcDate = new Date(utcString);
  const offset = -utcDate.getTimezoneOffset();
  const offsetHours = Math.abs(Math.floor(offset / 60)).toString().padStart(2, '0');
  const offsetMinutes = Math.abs(offset % 60).toString().padStart(2, '0');
  const offsetSign = offset >= 0 ? '+' : '-';
  
  return utcDate.getFullYear() +
    '-' + (utcDate.getMonth() + 1).toString().padStart(2, '0') +
    '-' + utcDate.getDate().toString().padStart(2, '0') +
    'T' + utcDate.getHours().toString().padStart(2, '0') +
    ':' + utcDate.getMinutes().toString().padStart(2, '0') +
    ':' + utcDate.getSeconds().toString().padStart(2, '0') +
    offsetSign + offsetHours + ':' + offsetMinutes;
} 